// src/components/AppHeader.tsx
import React from 'react';
import { AppBar, Toolbar, Typography, Box, Button } from '@mui/material';
import { Logout as LogoutIcon } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { SxProps, Theme } from '@mui/material/styles';
import logo from './logo.svg';

interface AppHeaderProps {
  title?: string;
  children?: React.ReactNode;
  position?: 'static' | 'fixed' | 'absolute' | 'relative' | 'sticky';
  sx?: SxProps<Theme>;
}

/**
 * Shared branded AppHeader - use on every page for consistent branding.
 * Shows persistent nav links: Cost Calculator, My Quotes, Admin (admin only).
 */
const AppHeader = ({ children, position = 'static', sx = {} }: AppHeaderProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const isActive = (path: string) => location.pathname.startsWith(path);

  return (
    <AppBar
      position={position}
      elevation={1}
      sx={{ backgroundColor: 'primary.main', color: 'primary.contrastText', ...(sx as Record<string, unknown>) }}
    >
      <Toolbar>
        {/* Logo - click navigates home */}
        <Box
          component="img"
          src={logo}
          alt="Logo"
          onClick={() => navigate('/')}
          sx={{
            height: 34,
            mr: 2,
            cursor: 'pointer',
          }}
        />

        {/* Persistent nav links */}
        <Button
          color="inherit"
          onClick={() => navigate('/quote-builder')}
          sx={{ fontWeight: isActive('/quote-builder') ? 700 : 400, mr: 1 }}
        >
          Cost Calculator
        </Button>
        <Button
          color="inherit"
          onClick={() => navigate('/my-quotes')}
          sx={{ fontWeight: isActive('/my-quotes') ? 700 : 400, mr: 1 }}
        >
          My Quotes
        </Button>
        {user?.isAdmin && (
          <Button
            color="inherit"
            onClick={() => navigate('/admin')}
            sx={{ fontWeight: isActive('/admin') ? 700 : 400, mr: 1 }}
          >
            Admin
          </Button>
        )}

        {/* Optional extra content (e.g. back button) injected by the page */}
        {children}

        <Box sx={{ flexGrow: 1 }} />

        {/* User info + logout */}
        {user && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {user.fullName || user.username}
            </Typography>
            <Button
              color="inherit"
              size="small"
              startIcon={<LogoutIcon />}
              onClick={logout}
              sx={{ ml: 1, opacity: 0.9 }}
            >
              Logout
            </Button>
          </Box>
        )}
      </Toolbar>

      {/* Thin yellow accent bar - Aptean branding */}
      <Box sx={{ height: 3, backgroundColor: 'secondary.main' }} />
    </AppBar>
  );
};

export default AppHeader;
